import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, apiGet } from '../../api/client'
import {
  alarmsMatrix,
  alarmsQuery,
  changeAlarmStatus,
  checkAlarmValue,
  createAlarm,
  createAlarmForm,
  createGroup,
  editAlarmForm,
  getAlarmsType,
  loadDefaultSettings,
  loadFactoryEmpty,
  removeAlarm,
  removeGroup,
  saveThresholds,
  updateAlarm,
  updateGroup,
  type AlarmSignal,
} from '../../api/settingsProactivitee'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

function num(row: Record<string, unknown> | null | undefined, key: string): number {
  return Number(row?.[key] ?? 0)
}

function str(row: Record<string, unknown> | null | undefined, key: string): string {
  return String(row?.[key] ?? '')
}

function flagLabel(flag: number): string {
  if (flag === 3) return 'Critical'
  if (flag === 2) return 'Major'
  if (flag === 1) return 'Minor'
  return 'Info'
}

function alarmFlag(flag: unknown) {
  switch (String(flag)) {
    case '0':
      return <label className="label label-primary"><i className="far fa-bell"></i> info</label>
    case '1':
      return <label className="label label-success"><i className="far fa-bell"></i> minor</label>
    case '2':
      return <label className="label label-warning"><i className="far fa-bell"></i> major</label>
    case '3':
      return <label className="label label-danger"><i className="far fa-bell"></i> critical</label>
    default:
      return null
  }
}

function sentenceFor(layer: string, groupName: string): string {
  if (layer === 'sp') {
    return 'Global. Companies without their own bands inherit these. To silence a signal on one machine, use Now on that device.'
  }
  if (layer === 'group') {
    const name = groupName || 'this group'
    return 'Groups · «' + name + '». These bands replace the company ones for machines in that group. Disable a signal on one machine from Now, on the device.'
  }
  return 'Company. Machines assigned to a group use that group instead. Disable a signal on one machine from Now, on the device.'
}

export function LiveAlarmsEditor({ door = 'proactivitee' }: { door?: string } = {}) {
  const query = useQuery(alarmsQuery(door))
  if (query.error instanceof ApiError && query.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!query.data) return <Spinner />
  return <AlarmDoor page={query.data} />
}

function AlarmDoor({ page }: { page: Record<string, unknown> }) {
  const client = useQueryClient()
  const canSp = Boolean(page.canSp)
  const canCompany = Boolean(page.canCompany)
  const canGroups = Boolean(page.canGroups)
  const door = String(page.door ?? 'proactivitee')
  const defaultLayer = String(page.defaultLayer ?? (canSp ? 'sp' : canCompany ? 'company' : 'group'))
  const initialGroups = (page.groups as Array<Record<string, unknown>>) ?? []
  const [layer, setLayer] = useState(defaultLayer)
  const [groupId, setGroupId] = useState(() => num(initialGroups[0], 'ID'))
  const [groups, setGroups] = useState(initialGroups)
  const [selectedType, setSelectedType] = useState(0)
  const [signals, setSignals] = useState<AlarmSignal[] | null>(null)
  const [matrixError, setMatrixError] = useState('')
  const [factoryBusy, setFactoryBusy] = useState(false)
  const [groupsOpen, setGroupsOpen] = useState(false)
  const groupName = groups.find((g) => num(g, 'ID') === groupId)?.group_name
  const empty = (signals ?? []).filter((s) => !s.loaded).length
  const sp = layer === 'sp' ? Number(page.sp ?? 0) : 0
  const layerGroup = layer === 'group' ? groupId : 0

  const loadMatrix = async () => {
    setMatrixError('')
    setSignals(null)
    try {
      const data = await alarmsMatrix(door, layer, layerGroup)
      setSignals(data.signals ?? [])
    } catch {
      setMatrixError('Could not load the signal list.')
      setSignals([])
    }
  }

  useEffect(() => {
    void loadMatrix()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer, layerGroup, door])

  const onFactory = async () => {
    if (factoryBusy || empty < 1) return
    setFactoryBusy(true)
    try {
      const res = await loadFactoryEmpty({ door, layer, group: layerGroup })
      const n = Number(res.seeded ?? 0)
      if (n > 0) toast(n + ' signal' + (n === 1 ? '' : 's') + ' loaded from factory', 'Alarm settings', 'success')
      else toast('Every signal already has bands', 'Alarm settings', 'info')
      await loadMatrix()
    } catch {
      toast('Could not load factory bands', 'Alarm settings', 'error')
    } finally {
      setFactoryBusy(false)
    }
  }

  const onManageGroups = () => {
    if (groupsOpen) {
      setGroupsOpen(false)
      const next = ((client.getQueryData(['settings', 'proactivitee', 'alarms']) as { groups?: Array<Record<string, unknown>> } | undefined)?.groups) ?? groups
      setGroups(next)
      if (next.length && !next.some((g) => num(g, 'ID') === groupId)) {
        setGroupId(num(next[0], 'ID'))
      }
      return
    }
    setGroupsOpen(true)
  }

  return (
    <div className="ad-page">
      <input type="hidden" id="sp" value={String(page.sp ?? 0)} />
      <h3 className="ad-title">{String(page.title ?? 'Live alarms')}</h3>
      <p className="ad-blurb">{String(page.blurb ?? '')}</p>

      <div className="ad-card" id="alarm-door" data-layer={layer} data-door={door}>
        <div className="ad-step">1. Who these thresholds apply to</div>
        <div className="ad-layers">
          {canSp ? (
            <button type="button" className={layer === 'sp' ? 'ad-layer is-on' : 'ad-layer'} data-layer="sp" onClick={() => setLayer('sp')}>
              Global
            </button>
          ) : null}
          {canCompany ? (
            <button type="button" className={layer === 'company' ? 'ad-layer is-on' : 'ad-layer'} data-layer="company" onClick={() => setLayer('company')}>
              Company
            </button>
          ) : null}
          {canGroups ? (
            <>
              <button type="button" className={layer === 'group' ? 'ad-layer is-on' : 'ad-layer'} data-layer="group" onClick={() => setLayer('group')}>
                Groups
              </button>
              <span className={layer === 'group' ? 'ad-group-wrap is-on' : 'ad-group-wrap'} id="ad-group-wrap">
                <select id="ad-group-id" value={String(groupId)} onChange={(e) => setGroupId(Number(e.target.value))}>
                  {groups.length === 0 ? <option value="0">No groups yet</option> : null}
                  {groups.map((g) => (
                    <option key={String(g.ID)} value={String(g.ID)}>
                      {str(g, 'group_name')}
                    </option>
                  ))}
                </select>
                <button type="button" id="ad-manage-groups" onClick={onManageGroups}>
                  Create or rename
                </button>
              </span>
            </>
          ) : null}
        </div>
        <p className="ad-sentence" id="ad-sentence">
          {sentenceFor(layer, String(groupName ?? ''))}
        </p>
      </div>

      <div id="ad-groups-panel" className={groupsOpen ? 'is-on' : undefined} style={groupsOpen ? { display: 'block' } : undefined}>
        {groupsOpen ? <GroupsPanel onChanged={(next) => setGroups(next)} /> : null}
      </div>

      <div className="ad-workspace">
        <div className="ad-matrix-toolbar">
          <div className="ad-step">2. Every signal — watch and threshold</div>
          <button
            type="button"
            className="ad-factory-btn"
            id="ad-load-factory"
            disabled={factoryBusy || empty < 1}
            onClick={() => void onFactory()}
          >
            {factoryBusy ? 'Loading…' : 'Load factory bands for empty signals'}
          </button>
        </div>
        <div className="ad-matrix-wrap">
          <table className="ad-matrix" id="ad-matrix">
            <thead>
              <tr>
                <th>Signal</th>
                <th style={{ width: 110 }}>Watch</th>
                <th>Bands</th>
              </tr>
            </thead>
            <tbody>
              {signals === null ? (
                <tr>
                  <td colSpan={3}>Loading…</td>
                </tr>
              ) : matrixError ? (
                <tr>
                  <td colSpan={3}>{matrixError}</td>
                </tr>
              ) : signals.length === 0 ? (
                <tr>
                  <td colSpan={3}>No signals on this door.</td>
                </tr>
              ) : (
                <MatrixRows signals={signals} selectedType={selectedType} onPick={setSelectedType} />
              )}
            </tbody>
          </table>
        </div>
        <div className="ad-bands" id="alarms-nav-container">
          {selectedType > 0 ? (
            <AlarmsView type={selectedType} group={layerGroup} sp={sp} onChanged={() => void loadMatrix()} />
          ) : (
            <p className="ad-bands-hint">Pick a signal to edit its bands.</p>
          )}
        </div>
      </div>

      {page.showSaturation ? <ColoursPanel page={page} /> : null}
    </div>
  )
}

function MatrixRows({
  signals,
  selectedType,
  onPick,
}: {
  signals: AlarmSignal[]
  selectedType: number
  onPick: (id: number) => void
}) {
  let lastChapter = ''
  const rows: Array<{ kind: 'chapter' | 'signal'; chapter?: string; signal?: AlarmSignal }> = []
  for (const row of signals) {
    if (row.chapter && row.chapter !== lastChapter) {
      lastChapter = row.chapter
      rows.push({ kind: 'chapter', chapter: row.chapter })
    }
    rows.push({ kind: 'signal', signal: row })
  }
  return (
    <>
      {rows.map((row, i) =>
        row.kind === 'chapter' ? (
          <tr key={'ch-' + row.chapter + '-' + i} className="ad-chapter-row">
            <td colSpan={3}>{row.chapter}</td>
          </tr>
        ) : (
          <SignalRow key={row.signal!.id} row={row.signal!} selected={selectedType === row.signal!.id} onPick={onPick} />
        ),
      )}
    </>
  )
}

function SignalRow({ row, selected, onPick }: { row: AlarmSignal; selected: boolean; onPick: (id: number) => void }) {
  const chips =
    !row.bands || row.bands.length === 0 ? (
      <span className="ad-chip ad-chip-off">No factory bands</span>
    ) : (
      row.bands.map((band, j) => {
        const cls = band.on && !band.factory ? 'ad-chip ad-chip-' + band.flag : 'ad-chip ad-chip-off'
        let range = band.dinamic ? String(band.from) + '–' + String(band.to) + (row.sign || '') : flagLabel(band.flag)
        if (band.factory) range = 'Factory ' + range
        return (
          <span key={j} className={cls}>
            {range}
          </span>
        )
      })
    )
  const watch = row.watched ? (
    <span className="ad-watch is-on">On</span>
  ) : row.loaded ? (
    <span className="ad-watch is-off">Off</span>
  ) : (
    <span className="ad-watch is-off">Not loaded</span>
  )
  return (
    <tr data-type={row.id} className={selected ? 'is-on' : undefined} onClick={() => onPick(row.id)}>
      <td>
        <strong>{row.name}</strong>
        {row.hint ? (
          <span className="ad-hint" tabIndex={0} role="img" aria-label={row.hint} title={row.hint}>
            i
          </span>
        ) : null}
      </td>
      <td>{watch}</td>
      <td>{chips}</td>
    </tr>
  )
}

function AlarmsView({ type, group, sp, onChanged }: { type: number; group: number; sp: number; onChanged: () => void }) {
  const [data, setData] = useState<{
    default: boolean
    alertType: Record<string, unknown> | null
    alarms: Array<Record<string, unknown>>
    inheritedFrom: string
  } | null>(null)
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
  const [editId, setEditId] = useState(0)
  const [unlocked, setUnlocked] = useState(false)

  const reload = async () => {
    setData(null)
    setMode('list')
    setUnlocked(false)
    const next = await getAlarmsType(type, group, sp)
    setData(next)
  }

  useEffect(() => {
    setData(null)
    setMode('list')
    setUnlocked(false)
    void getAlarmsType(type, group, sp).then(setData)
  }, [type, group, sp])

  if (!data) return <Spinner />
  if (mode === 'create') {
    return <CreateAlarmForm type={type} group={group} sp={sp} onCancel={() => void reload()} onSaved={() => { void reload(); onChanged() }} />
  }
  if (mode === 'edit') {
    return <EditAlarmForm alarmId={editId} onCancel={() => void reload()} onSaved={() => { void reload(); onChanged() }} />
  }

  const alertType = data.alertType
  const inherited = data.inheritedFrom && !unlocked
  return (
    <div className={inherited ? 'ad-locked' : undefined}>
      <div className="alarms-header">
        <h4>
          {str(alertType, 'type_name')}
          {num(alertType, 'dinamic') === 1 ? (
            <button
              className="btn btn-sm btn-success pull-right"
              id="create-alarm-btn"
              type="button"
              data-alert={type}
              data-group={group}
              data-sp={sp}
              onClick={() => setMode('create')}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> New alarm
            </button>
          ) : null}
        </h4>
        <span className="help-block">{str(alertType, 'alert_type_description')}</span>
      </div>
      <hr />
      <AlarmsList
        data={data}
        group={group}
        sp={sp}
        unlocked={unlocked}
        onUnlock={() => setUnlocked(true)}
        onEdit={(id) => {
          setEditId(id)
          setMode('edit')
        }}
        onReload={() => {
          void reload()
          onChanged()
        }}
      />
    </div>
  )
}

function AlarmsList({
  data,
  group,
  sp,
  unlocked,
  onUnlock,
  onEdit,
  onReload,
}: {
  data: { default: boolean; alertType: Record<string, unknown> | null; alarms: Array<Record<string, unknown>>; inheritedFrom: string }
  group: number
  sp: number
  unlocked: boolean
  onUnlock: () => void
  onEdit: (id: number) => void
  onReload: () => void
}) {
  if (data.default) {
    return (
      <>
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No settings for this alarm type. You can load default settings or create a new alarm
        </div>
        <button
          className="btn btn-sm btn-primary"
          id="load-default-settings"
          type="button"
          data-type={num(data.alertType, 'ID')}
          data-group={group}
          data-sp={sp}
          onClick={() => {
            void loadDefaultSettings({ type: num(data.alertType, 'ID'), group, sp }).then((res) => {
              if ((res.loaded ?? 0) > 0) {
                toast('Default settings loaded', 'Alarm settings', 'success')
                onReload()
              } else {
                toast('There are no default settings defined for this alarm type', 'Alarm settings', 'error')
              }
            })
          }}
        >
          Load default settings
        </button>
      </>
    )
  }

  const inherited = Boolean(data.inheritedFrom)
  const showLock = inherited && !unlocked
  return (
    <>
      {inherited ? (
        showLock ? (
          <>
            <div className="alert alert-info ad-inherited">
              <i className="fas fa-level-down-alt" aria-hidden="true"></i> Same bands as <strong>{data.inheritedFrom}</strong>. Change them here only if this level needs different ones.
            </div>
            <button type="button" className="btn btn-sm btn-primary ad-change-inherited" onClick={onUnlock}>
              Change
            </button>
          </>
        ) : (
          <div className="alert alert-warning ad-inherited">
            <i className="fas fa-pen" aria-hidden="true"></i> Editing this level. Once saved, these stop following {data.inheritedFrom}.
          </div>
        )
      ) : null}
      {data.alarms.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle" aria-hidden="true"></i> No alarms
        </div>
      ) : num(data.alertType, 'dinamic') === 1 ? (
        <DynamicAlarms alarms={data.alarms} alertType={data.alertType} onEdit={onEdit} onReload={onReload} />
      ) : (
        <SingleAlarm alarm={data.alarms[0]} onEdit={onEdit} />
      )}
    </>
  )
}

function SingleAlarm({ alarm, onEdit }: { alarm: Record<string, unknown>; onEdit: (id: number) => void }) {
  const triggers = (alarm.triggers as Array<Record<string, unknown>>) ?? []
  return (
    <div className="row">
      <div className="col-md-3">
        <label style={{ display: 'block' }}>Activate</label>
        <input
          type="checkbox"
          className="activate-alarm"
          data-alarm={num(alarm, 'ID')}
          defaultChecked={num(alarm, 'status') === 1}
          onChange={(e) => {
            void changeAlarmStatus(num(alarm, 'ID'), e.target.checked ? 1 : 0).then(() => toast('Alarm status changed', 'Alarm settings', 'success'))
          }}
        />
      </div>
      <div className="col-md-4">
        <label style={{ display: 'block' }}>Severity</label>
        {alarmFlag(alarm.flag)}
      </div>
      <div className="col-md-4">
        <label style={{ display: 'block' }}>Triggers</label>
        {triggers.map((t, i) => (
          <p key={i} className="">
            <strong>{str(t, 'event_action').toUpperCase()}</strong> : <span className="color-primary">{str(t, 'trigger_name')}</span>
          </p>
        ))}
      </div>
      <div className="col-md-1">
        <label style={{ display: 'block' }}>Actions</label>
        <button className="btn btn-primary btn-sm edit-alarm" type="button" data-alarm={num(alarm, 'ID')} data-alarmtype={num(alarm, 'alert_type')} onClick={() => onEdit(num(alarm, 'ID'))}>
          Edit
        </button>
      </div>
    </div>
  )
}

function DynamicAlarms({
  alarms,
  alertType,
  onEdit,
  onReload,
}: {
  alarms: Array<Record<string, unknown>>
  alertType: Record<string, unknown> | null
  onEdit: (id: number) => void
  onReload: () => void
}) {
  const sign = str(alertType, 'sign')
  return (
    <table className="table table-hover" width="100%">
      <thead style={{ backgroundColor: '#FFF' }}>
        <tr>
          <th>Activate</th>
          <th>Severity</th>
          <th>Thresholds</th>
          <th>Triggers</th>
          <th>Manually close</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody id="alarms-details">
        {alarms.map((alarm) => {
          const triggers = (alarm.triggers as Array<Record<string, unknown>>) ?? []
          return (
            <tr key={String(alarm.ID)} className={'alarm_settings_' + num(alarm, 'ID')}>
              <td style={{ padding: 10, width: 30 }}>
                <input
                  type="checkbox"
                  className="activate-alarm"
                  data-alarm={num(alarm, 'ID')}
                  defaultChecked={num(alarm, 'status') === 1}
                  onChange={(e) => {
                    void changeAlarmStatus(num(alarm, 'ID'), e.target.checked ? 1 : 0).then(() => toast('Alarm status changed', 'Alarm settings', 'success'))
                  }}
                />
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>{alarmFlag(alarm.flag)}</td>
              <td style={{ padding: 10, textAlign: 'center' }}>
                MIN : <label className="label label-default">{str(alarm, 'from_value')}{sign}</label>
                {' - MAX : '}
                <label className="label label-default">{str(alarm, 'to_value')}{sign}</label>
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>
                {triggers.map((t, i) => (
                  <p key={i} className="">
                    <strong>{str(t, 'event_action').toUpperCase()}</strong> : <span className="color-primary">{str(t, 'trigger_name')}</span>
                  </p>
                ))}
              </td>
              <td style={{ padding: 10, textAlign: 'center' }}>
                {num(alarm, 'close_manually') === 1 ? (
                  <i className="fas fa-check-circle color-success font-16"></i>
                ) : (
                  <i className="fas fa-times-circle color-danger font-16"></i>
                )}
              </td>
              <td style={{ padding: 10, width: 125 }}>
                <button className="btn btn-primary btn-sm edit-alarm" type="button" data-alarm={num(alarm, 'ID')} data-alarmtype={num(alarm, 'alert_type')} onClick={() => onEdit(num(alarm, 'ID'))}>
                  Edit
                </button>{' '}
                <button
                  className="btn btn-danger btn-sm remove-alarm"
                  type="button"
                  data-alarm={num(alarm, 'ID')}
                  onClick={() => {
                    if (!confirm('Are you sure?\nData will be lost.')) return
                    void removeAlarm(num(alarm, 'ID'))
                      .then(() => {
                        toast('Alarm settings deleted.', 'Alarm settings', 'success')
                        onReload()
                      })
                      .catch(() => toast('Alarm settings could not be deleted.', 'Error deleting', 'error'))
                  }}
                >
                  Delete
                </button>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function fieldsFromForm(form: HTMLFormElement): Record<string, string> {
  const data = new FormData(form)
  const fields: Record<string, string> = {}
  data.forEach((value, key) => {
    fields[key] = String(value)
  })
  return fields
}

async function validateThresholds(fields: Record<string, string>): Promise<string> {
  const min = fields.minThreshold
  const max = fields.maxThreshold
  if (min === undefined && max === undefined) return ''
  if (min !== undefined && max !== undefined && Number(min) > Number(max)) {
    return 'Field value must be lower than Min threshold'
  }
  const res = await checkAlarmValue({
    minThreshold: min ?? '',
    maxThreshold: max ?? '',
    alarmType: fields['alarm-type'] ?? '',
    alarmId: fields['alarm-id'] ?? '',
    alarmGroup: fields['alarm-group'] ?? '',
    alarmSp: fields['alarm-sp'] ?? '',
  })
  if (res.valid === 'false') {
    return 'There is a similar value on an existing alert. To create a new alarm please use a different range of values.'
  }
  return ''
}

function TriggerFields({
  events,
  triggers,
  selected,
}: {
  events: Array<Record<string, unknown>>
  triggers: Array<Record<string, unknown>>
  selected?: Record<string, number>
}) {
  return (
    <div className="form-group">
      <label className="control-label" htmlFor="exampleInputPassword1">Triggers</label>
      <div className="row">
        <div className="col-md-6">
          {events.map((event) => (
            <div key={String(event.ID)}>
              <div className="input-group">
                <span className="input-group-addon">{str(event, 'event_action').charAt(0).toUpperCase() + str(event, 'event_action').slice(1)}</span>
                <select className="form-control" name={'triggers[' + num(event, 'ID') + ']'} defaultValue={selected ? String(selected[String(event.ID)] ?? selected[num(event, 'ID')] ?? '') : ''}>
                  <option value="">No trigger</option>
                  {triggers.map((trigger) => (
                    <option key={String(trigger.ID)} value={String(trigger.ID)}>
                      {str(trigger, 'trigger_name')}
                    </option>
                  ))}
                </select>
              </div>
              <br />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function CreateAlarmForm({
  type,
  group,
  sp,
  onCancel,
  onSaved,
}: {
  type: number
  group: number
  sp: number
  onCancel: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState<{
    alarm: Record<string, unknown> | null
    triggers: Array<Record<string, unknown>>
    events: Array<Record<string, unknown>>
  } | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    void createAlarmForm(type, group, sp).then(setForm)
  }, [type, group, sp])
  if (!form) return <Spinner />
  const alarm = form.alarm
  const dinamic = num(alarm, 'dinamic') === 1
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fields = fieldsFromForm(event.currentTarget)
    const invalid = await validateThresholds(fields)
    if (invalid) {
      setError(invalid)
      return
    }
    try {
      await createAlarm(fields)
      toast('New alarm settings created', 'Alarm settings', 'success')
      onSaved()
    } catch {
      toast('Something went wrong', 'Alarm settings', 'error')
    }
  }
  return (
    <>
      <h4>Create alarm</h4>
      <br />
      <form action="/settings/proactivitee/create_alarm" id="create-alarm-form" method="post" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-group">
          <label htmlFor="exampleInputEmail1">Severity</label>
          <div className="row">
            <div className="col-md-6">
              {group > 0 ? <input type="hidden" name="alarm-group" value={String(group)} /> : null}
              {sp > 0 ? <input type="hidden" name="alarm-sp" value={String(sp)} /> : null}
              <input type="hidden" name="alarm-type" value={String(num(alarm, 'ID'))} />
              <select className="form-control" name="alarm-severity">
                <option value="0">Info</option>
                <option value="1">Minor</option>
                <option value="2">Major</option>
                <option value="3">Critical</option>
              </select>
            </div>
          </div>
        </div>
        {dinamic ? (
          <div className="form-group">
            <label className="control-label" htmlFor="exampleInputPassword1">Thresholds</label>
            <div className="row">
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon">Min</span>
                  <input className="form-control" name="minThreshold" type="number" defaultValue={0} required />
                  <span className="input-group-addon">{str(alarm, 'sign')}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon">Max</span>
                  <input className="form-control" name="maxThreshold" type="number" defaultValue={0} required />
                  <span className="input-group-addon">{str(alarm, 'sign')}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <input type="hidden" name="minThreshold" value="0" />
            <input type="hidden" name="maxThreshold" value="0" />
          </>
        )}
        <TriggerFields events={form.events} triggers={form.triggers} />
        <div className="form-group">
          <label htmlFor="exampleInputEmail1">Alarm description (optional)</label>
          <div className="row">
            <div className="col-md-6">
              <input type="text" name="alarm_description" className="form-control" />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="activate" value="on" /> Activate
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="close_manually" value="on" /> Close only manually
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="adam_autoresolve" value="on" /> Autoresolve with Adam Intop
              </label>
            </div>
          </div>
        </div>
        <div id="form-errors">{error}</div>
        <button type="submit" className="btn btn-primary">Save</button>{' '}
        <button type="button" className="btn btn-default" id="cancel-create-form" data-type={num(alarm, 'ID')} onClick={onCancel}>
          Cancel
        </button>
      </form>
    </>
  )
}

function EditAlarmForm({ alarmId, onCancel, onSaved }: { alarmId: number; onCancel: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<{
    alarm: Record<string, unknown>
    alarmType: Record<string, unknown> | null
    triggers: Array<Record<string, unknown>>
    events: Array<Record<string, unknown>>
    alarmTriggers: Record<string, number>
  } | null>(null)
  const [error, setError] = useState('')
  useEffect(() => {
    void editAlarmForm(alarmId).then(setForm)
  }, [alarmId])
  if (!form) return <Spinner />
  const alarm = form.alarm
  const dinamic = num(alarm, 'dinamic') !== 0
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fields = fieldsFromForm(event.currentTarget)
    const invalid = await validateThresholds(fields)
    if (invalid) {
      setError(invalid)
      return
    }
    try {
      await updateAlarm(fields)
      toast('Alarm settings updated', 'Alarm settings', 'success')
      onSaved()
    } catch {
      toast('Something went wrong', 'Alarm settings', 'error')
    }
  }
  return (
    <>
      <h4>Edit alarm</h4>
      <br />
      <form action="/settings/proactivitee/update_default_alarm" id="edit-default-alarm-form" method="post" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-group">
          <label htmlFor="exampleInputEmail1">Severity</label>
          <div className="row">
            <div className="col-md-6">
              {num(alarm, 'group_id') > 0 ? <input type="hidden" name="alarm-group" value={String(num(alarm, 'group_id'))} /> : null}
              {num(alarm, 'service_provider') > 0 ? <input type="hidden" name="alarm-sp" value={String(num(alarm, 'service_provider'))} /> : null}
              <input type="hidden" name="alarm-type" value={String(num(alarm, 'alert_type'))} />
              <input type="hidden" name="alarm-id" value={String(num(alarm, 'ID'))} />
              <select className="form-control" name="alarm-severity" defaultValue={String(num(alarm, 'flag'))}>
                <option value="0">Info</option>
                <option value="1">Minor</option>
                <option value="2">Major</option>
                <option value="3">Critical</option>
              </select>
            </div>
          </div>
        </div>
        {dinamic ? (
          <div className="form-group">
            <label className="control-label" htmlFor="exampleInputPassword1">Thresholds</label>
            <div className="row">
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon">Min</span>
                  <input className="form-control" name="minThreshold" type="number" defaultValue={str(alarm, 'from_value')} required />
                  <span className="input-group-addon">{str(form.alarmType, 'sign')}</span>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon">Max</span>
                  <input className="form-control" name="maxThreshold" type="number" defaultValue={str(alarm, 'to_value')} required />
                  <span className="input-group-addon">{str(form.alarmType, 'sign')}</span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <TriggerFields events={form.events} triggers={form.triggers} selected={form.alarmTriggers} />
        <div className="form-group">
          <label htmlFor="exampleInputEmail1">Alarm description (optional)</label>
          <div className="row">
            <div className="col-md-6">
              <input type="text" name="alarm_description" defaultValue={str(alarm, 'alarm_description')} className="form-control" />
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="activate" value="on" defaultChecked={num(alarm, 'status') === 1} /> Activate
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="close_manually" value="on" defaultChecked={num(alarm, 'close_manually') === 1} /> Close only manually
              </label>
            </div>
          </div>
        </div>
        <div className="form-group">
          <div className="row">
            <div className="col-md-6">
              <label>
                <input type="checkbox" className="icheck-alarm" name="adam_autoresolve" value="on" defaultChecked={num(alarm, 'adam_autoresolve') === 1} /> Autoresolve with Adam Intop
              </label>
            </div>
          </div>
        </div>
        <div id="form-errors">{error}</div>
        <button type="submit" className="btn btn-primary">Save</button>{' '}
        <button type="button" className="btn btn-default" id="cancel-edit-form" data-type={num(alarm, 'alert_type')} onClick={onCancel}>
          Cancel
        </button>
      </form>
    </>
  )
}

function GroupsPanel({ onChanged }: { onChanged: (groups: Array<Record<string, unknown>>) => void }) {
  const [groups, setGroups] = useState<Array<Record<string, unknown>> | null>(null)
  const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
  const [edit, setEdit] = useState<Record<string, unknown> | null>(null)
  const reload = async () => {
    const page = await apiGet<{ groups: Array<Record<string, unknown>> }>('/settings/proactivitee/alarm-groups')
    setGroups(page.groups)
    onChanged(page.groups)
    setMode('list')
  }
  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (!groups) return <Spinner />
  if (mode === 'create') {
    return (
      <form
        id="create-group-form"
        action="/settings/proactivitee/create_group"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          const name = String(new FormData(e.currentTarget).get('group_name') ?? '')
          void createGroup(name).then(() => {
            toast('Group created successfully', 'Alarm groups', 'success')
            void reload()
          })
        }}
      >
        <div className="form-group">
          <label htmlFor="disabledTextInput">Group name</label>
          <input className="form-control" type="text" name="group_name" />
        </div>
        <button className="btn btn-default" type="button" id="cancel-update-group" onClick={() => setMode('list')}>
          Cancel
        </button>{' '}
        <button className="btn btn-primary" type="submit">
          Create
        </button>
      </form>
    )
  }
  if (mode === 'edit' && edit) {
    return (
      <form
        id="update-group-form"
        action="/settings/proactivitee/update_group"
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          const data = new FormData(e.currentTarget)
          void updateGroup(Number(data.get('group')), String(data.get('group_name') ?? '')).then(() => {
            toast('Group updated successfully', 'Alarm groups', 'success')
            void reload()
          })
        }}
      >
        <div className="form-group">
          <input type="hidden" name="group" value={String(edit.ID)} />
          <label htmlFor="disabledTextInput">Group name</label>
          <input className="form-control" type="text" name="group_name" defaultValue={str(edit, 'group_name')} />
        </div>
        <button className="btn btn-default" type="button" id="cancel-update-group" onClick={() => setMode('list')}>
          Cancel
        </button>{' '}
        <button className="btn btn-primary" type="submit">
          Update
        </button>
      </form>
    )
  }
  return (
    <>
      <h4>
        <strong>Alarm</strong> groups
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Create and edit PROACTIVITEE <strong>alarm groups</strong>
      </label>
      <button className="btn btn-success pull-right" id="create-alarm-group" type="button" onClick={() => setMode('create')}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create group
      </button>
      <hr />
      <div id="groups-content">
        {groups.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle"></i> No groups found
          </div>
        ) : (
          <div className="list-group">
            {groups.map((g) => (
              <a key={String(g.ID)} href="#" className="list-group-item" onClick={(e) => e.preventDefault()}>
                <i className="fa fa-cube font-14" aria-hidden="true"></i>{' '}
                <span className="font-14 color-blue font-mont">{str(g, 'group_name')}</span>
                <div className="pull-right" style={{ marginTop: -5 }}>
                  <button className="btn btn-sm btn-primary edit-group" type="button" data-group={num(g, 'ID')} onClick={() => { setEdit(g); setMode('edit') }}>
                    Edit
                  </button>{' '}
                  <button
                    className="btn btn-sm btn-danger remove-group"
                    type="button"
                    data-group={num(g, 'ID')}
                    onClick={() => {
                      if (!confirm('Are you sure?\nData will be lost.')) return
                      void removeGroup(num(g, 'ID')).then(() => {
                        toast('Group deleted.', 'Alarm groups', 'success')
                        void reload()
                      })
                    }}
                  >
                    Remove
                  </button>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

function ColoursPanel({ page }: { page: Record<string, unknown> }) {
  const client = useQueryClient()
  const pairs = (page.pairs as Array<Record<string, string>>) ?? []
  const thresholds = (page.thresholds as Record<string, unknown>) ?? {}
  const canSave = Boolean(page.canSaveThresholds)
  const onSave = async () => {
    const form = document.getElementById('thresholdsForm') as HTMLFormElement | null
    if (!form) return
    const fields = fieldsFromForm(form)
    const res = await saveThresholds(fields)
    toast(res.message ?? 'Thresholds saved.', 'Thresholds', res.status === 'OK' ? 'success' : 'error')
    await client.invalidateQueries({ queryKey: ['settings', 'proactivitee', 'alarms'] })
  }
  return (
    <div className="ad-colours">
      <div className="ad-step" style={{ marginBottom: 10 }}>
        3. Colours on Performance — not tickets
      </div>
      <div className="portlet light bg-inverse bordered-left border-mauve">
        <div className="portlet-title">
          <div className="caption color-mauve">
            <i className="fas fa-sliders-h"></i>
            <span className="caption-subject bold uppercase">Performance colours</span>
            <span className="caption-helper">paint the live figures — they do not open an alarm</span>
          </div>
          {canSave ? (
            <div className="actions">
              <button type="button" className="btn btn-primary btn-xs" id="saveThresholds" onClick={() => void onSave()}>
                Update
              </button>
            </div>
          ) : null}
        </div>
        <div className="portlet-body">
          {!canSave ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle"></i> These are read-only for you.
            </div>
          ) : null}
          <p className="text-muted">
            These numbers paint CPU, RAM and disk on the Performance screen. Saving them also writes the matching Live alarm bands — a ticket still needs the type watched, above.
          </p>
          <div className="alert alert-danger" id="thresholdsError" style={{ display: 'none' }}></div>
          <form id="thresholdsForm">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Measure</th>
                  <th style={{ width: 160 }}>Warning at</th>
                  <th style={{ width: 160 }}>Critical at</th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair) => (
                  <tr key={pair.metric}>
                    <td>
                      <strong>{pair.label}</strong>
                      {pair.unit ? <span className="text-muted"> ({pair.unit})</span> : null}
                    </td>
                    <td>
                      <input className="form-control input-sm" type="number" step="0.01" min={0} name={pair.warn} defaultValue={String(thresholds[pair.warn] ?? '')} disabled={!canSave} />
                    </td>
                    <td>
                      <input className="form-control input-sm" type="number" step="0.01" min={0} name={pair.crit} defaultValue={String(thresholds[pair.crit] ?? '')} disabled={!canSave} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-muted small">
              A critical threshold below its warning is raised to match it when saved: written the other way round, the same value would be both &quot;over critical&quot; and &quot;under warning&quot;, and what you saw would depend on which check ran first.
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
